import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Heading, Text } from 'rebass';
import styled from '@emotion/styled';
import { CollectionAvatar } from 'collections/detail/header/index';

const AvatarButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const BookPill = styled.button`
  appearance: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  padding: 10px 14px;
  margin-top: 8px;
  border-radius: 8px;
  font-family: Poppins;
  font-size: 14px;
  background: #fff;
  border: 2px solid ${props => (props.selected ? '#9B59B6' : '#E2E8F0')};
  background: ${props => (props.selected ? '#F5EEF8' : '#fff')};

  border-radius: 10px;
  color: #000000;
  border-radius: 10px;
  font-weight: 600;
`;

// Two-level picker shown in the sidebar's child/book switcher: a row of the
// member's children (grouped by first name — see child-journals/group-by-child,
// the API has no real child/parent relationship), then the selected child's
// journals as "Book N" rows, oldest first.
const ChildBookSwitcher = ({ activeChildJournalName, childGroups, activeJournalId, onSelectJournal }) => {
  const activeChildKey = useMemo(() => {
    const activeGroup = childGroups.find(group =>
      group.journals.some(journal => journal.id === activeJournalId)
    );
    return activeGroup?.key;
  }, [childGroups, activeJournalId]);

  const [selectedJournal, setSelectedJournal] = useState({
    id: activeJournalId,
    name: activeChildJournalName,
  });

  useEffect(() => {
    setSelectedJournal({
      id: activeJournalId,
      name: activeChildJournalName,
    });
  }, [activeJournalId, activeChildJournalName]);

  const [selectedKey, setSelectedKey] = useState(activeChildKey);
  const selectedGroup =
    childGroups.find(group => group.key === selectedKey) || childGroups[0] || null;

  return (
    <Box padding={3}>
      <Text fontFamily="Poppins" fontWeight={400} lineHeight="165%" fontSize="20px" color="#000" textAlign="left" mb={1}>
        Child
      </Text>
      <Flex sx={{ gap: '16px', overflowX: 'auto' }} >
        {childGroups.map(group => {
          const isSelected = group.key === selectedGroup?.key;
          return (
            <AvatarButton
              key={group.key}
              type="button"
              onClick={() => {
                setSelectedJournal({
                  id: group?.journalId,
                  name: group?.firstName,
                });
                setSelectedKey(group.key)
              }}
              aria-pressed={isSelected}
            >
              <CollectionAvatar
                isFromSidebar
                id={isSelected ? selectedJournal?.id : group.journals[group.journals.length - 1].id}
                type="child-journals"
                size={48}
                showEdit={false}
                isSelected={isSelected}
              />
              <Text
                fontFamily="Poppins"
                fontWeight={600}
                fontSize="13px"
                color={isSelected ? '#9B59B6' : '#0E0E0E'}
              >
                {isSelected ? selectedJournal?.name : group.firstName}
              </Text>
            </AvatarButton>
          );
        })}
      </Flex>
      <Box
        my="20px"
        width="100%"
        height="1px"
        bg="#E2E8F0"
      />
      {selectedGroup && (
        <Box>
          <Heading fontFamily="Poppins" fontWeight={600} fontSize="14px" textAlign="left" color="#0E0E0E">
            {selectedJournal?.name}&rsquo;s Books
          </Heading>
          {selectedGroup.journals.map((journal, index) => (
            <BookPill
              key={journal.id}
              type="button"
              selected={journal.id === activeJournalId}
              aria-pressed={journal.id === activeJournalId}
              onClick={() => onSelectJournal(journal.id)}
            >
              Book {index + 1}
            </BookPill>
          ))}
        </Box>
      )}
    </Box>
  );
};

ChildBookSwitcher.propTypes = {
  childGroups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      journals: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
    })
  ).isRequired,
  activeJournalId: PropTypes.string,
  onSelectJournal: PropTypes.func.isRequired,
};

ChildBookSwitcher.defaultProps = {
  activeJournalId: null,
};

export default ChildBookSwitcher;
